import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "../../utils/cn";
import { useUI, type TableDensity } from "../../context/UIContext";
import { Dropdown, type DropdownMenuItem } from "../base/Dropdown";
import { Button } from "../base/Button";
import { EmptyState } from "../base/EmptyState";
import { ErrorState } from "../base/ErrorState";
import { SkeletonTable } from "../base/Skeleton";
import { Select, type SelectOption } from "../base/Select";

export interface DataTableColumn<T> {
  id: string;
  header: ReactNode;
  accessor: (row: T) => ReactNode;
  /** Numeric/string value used for sorting (defaults to accessor output). */
  sortValue?: (row: T) => string | number;
  /** Raw value used for filtering. */
  filterValue?: (row: T) => string | number;
  filterOptions?: SelectOption[];
  sortable?: boolean;
  filterable?: boolean;
  align?: "left" | "right" | "center";
  width?: string;
  hideable?: boolean;
  className?: string;
  headerClassName?: string;
  /** Value rendered in the totals row for this column. */
  footerValue?: (rows: T[]) => ReactNode;
}

export interface SortState {
  id: string;
  dir: "asc" | "desc";
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  /** null => loading skeleton. */
  rows: T[] | null;
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyMessage?: string;
  emptyDescription?: string;
  emptyAction?: { label: string; onClick: () => void };
  searchable?: boolean;
  searchPlaceholder?: string;
  globalSearch?: (row: T, query: string) => boolean;
  selectable?: boolean;
  onSelectionChange?: (rows: T[]) => void;
  onRowClick?: (row: T) => void;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  showDensityToggle?: boolean;
  showColumnVisibility?: boolean;
  exportable?: boolean;
  exportFilename?: string;
  onExport?: (rows: T[]) => void;
  showTotals?: boolean;
  totalLabel?: string;
  initialSort?: SortState;
  /** Seed the initial page (1-based) so list state can be restored across navigation. */
  initialPage?: number;
  initialPageSize?: number;
  /** Called whenever sort/page/pageSize change, so screens can persist list state. */
  onViewStateChange?: (state: { sort: SortState | null; page: number; pageSize: number }) => void;
  className?: string;
}

const DENSITY_ROW: Record<TableDensity, string> = {
  compact: "py-1.5",
  comfortable: "py-2.5",
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  onRetry,
  emptyMessage = "No records found",
  emptyDescription,
  emptyAction,
  searchable = false,
  searchPlaceholder = "Search records…",
  globalSearch,
  selectable = false,
  onSelectionChange,
  onRowClick,
  defaultPageSize = 10,
  pageSizeOptions = [5, 10, 25, 50],
  showDensityToggle = true,
  showColumnVisibility = true,
  exportable = false,
  exportFilename = "export",
  onExport,
  showTotals = false,
  totalLabel = "Total",
  initialSort,
  initialPage,
  initialPageSize,
  onViewStateChange,
  className,
}: DataTableProps<T>) {
  const ui = useUI();
  const [sort, setSort] = useState<SortState | null>(initialSort ?? null);
  const [page, setPage] = useState(initialPage ?? 1);
  const [pageSize, setPageSize] = useState(initialPageSize ?? defaultPageSize);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [density, setDensity] = useState<TableDensity>(ui.density);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [activeIndex, setActiveIndex] = useState(-1);
  const tableRef = useRef<HTMLDivElement>(null);

  const isDataLoading = loading ?? rows === null;

  // A column marked hideable: false must always render — it's excluded from
  // the visibility-toggle menu (line ~279's filter), not from the table
  // itself. Reusing that same "hideable !== false" predicate here inverted
  // the intent: it hid exactly the columns meant to be un-hideable (e.g. an
  // always-visible actions column) and only showed the ones the toggle menu
  // is allowed to turn off.
  const visibleColumns = useMemo(
    () => columns.filter((c) => c.hideable === false || !hiddenCols.has(c.id)),
    [columns, hiddenCols]
  );

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    let out = rows;
    if (query && globalSearch) out = out.filter((r) => globalSearch(r, query));
    else if (query) {
      const q = query.toLowerCase();
      out = out.filter((r) => columns.some((c) => {
        const v = c.sortValue ? c.sortValue(r) : (c.accessor(r) as unknown);
        return typeof v === "string" && v.toLowerCase().includes(q);
      }));
    }
    const activeFilters = Object.entries(filters).filter(([, v]) => v);
    if (activeFilters.length) {
      out = out.filter((r) =>
        activeFilters.every(([id, val]) => {
          const col = columns.find((c) => c.id === id);
          if (!col) return true;
          const raw = col.filterValue ? col.filterValue(r) : col.sortValue ? col.sortValue(r) : String(col.accessor(r));
          return String(raw) === val;
        })
      );
    }
    return out;
  }, [rows, query, filters, columns, globalSearch]);

  const sortedRows = useMemo(() => {
    if (!sort) return filteredRows;
    const col = columns.find((c) => c.id === sort.id);
    if (!col) return filteredRows;
    const get = col.sortValue ?? ((r: T) => String(col.accessor(r)));
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filteredRows, sort, columns]);

  const total = sortedRows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, safePage, pageSize]);

  const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selected.has(rowKey(r)));

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [safePage, page]);

  const onViewStateChangeRef = useRef(onViewStateChange);
  useEffect(() => {
    onViewStateChangeRef.current = onViewStateChange;
  });

  useEffect(() => {
    onViewStateChange?.({ sort, page, pageSize });
    // onViewStateChange is deliberately excluded: callers pass an inline
    // function, a new reference every render. Depending on it here would
    // refire this effect every render regardless of whether sort/page/
    // pageSize actually changed, which cascades into an infinite
    // render loop through the caller's own setState (see onSelectionChange
    // below for the same pattern).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, page, pageSize]);

  const toggleSort = (col: DataTableColumn<T>) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (!prev || prev.id !== col.id) return { id: col.id, dir: "asc" };
      return prev.dir === "asc" ? { id: col.id, dir: "desc" } : null;
    });
    setPage(1);
  };

  const setFilter = (id: string, value: string) => {
    setFilters((prev) => ({ ...prev, [id]: value }));
    setPage(1);
  };

  const toggleRow = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (!selectable || !rows) return;
    const selectedRows = rows.filter((r) => selected.has(rowKey(r)));
    onSelectionChange?.(selectedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, rows]);

  const toggleSelectPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageRows.forEach((r) => next.delete(rowKey(r)));
      else pageRows.forEach((r) => next.add(rowKey(r)));
      return next;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (pageRows.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i < pageRows.length - 1 ? i + 1 : i));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && onRowClick) {
        e.preventDefault();
        onRowClick(pageRows[activeIndex]);
      }
    }
  };

  const handleExport = () => {
    if (onExport) {
      onExport(sortedRows);
      return;
    }
    const header = visibleColumns.map((c) => `"${String(c.header).replace(/"/g, '""')}"`).join(",");
    const body = sortedRows
      .map((r) =>
        visibleColumns
          .map((c) => {
            const v = c.sortValue ? c.sortValue(r) : String(c.accessor(r));
            return `"${String(v).replace(/"/g, '""')}"`;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${exportFilename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const alignClass = (align?: "left" | "right" | "center") =>
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

  const columnMenuItems: DropdownMenuItem[] = columns
    .filter((c) => c.hideable !== false)
    .map((c) => {
      const hidden = hiddenCols.has(c.id);
      return {
        key: c.id,
        label: (
          <span className="flex items-center gap-2">
            <input type="checkbox" readOnly checked={!hidden} className="h-4 w-4 accent-primary-600" />
            {String(c.header)}
          </span>
        ),
        onClick: () =>
          setHiddenCols((prev) => {
            const next = new Set(prev);
            if (next.has(c.id)) next.delete(c.id);
            else next.add(c.id);
            return next;
          }),
      };
    });

  return (
    <div
      ref={tableRef}
      className={cn("rounded-lg border border-background-200 bg-background-50", className)}
      onKeyDown={handleKeyDown}
      tabIndex={onRowClick ? 0 : undefined}
      role={onRowClick ? "grid" : undefined}
    >
      {/* Toolbar */}
      {(searchable || showDensityToggle || showColumnVisibility || exportable) && (
        <div className="no-print flex flex-wrap items-center gap-2 border-b border-background-100 px-3 py-2.5">
          {searchable ? (
            <div className="relative w-full sm:w-64">
              <i className="ri-search-line pointer-events-none absolute inset-y-0 left-0 flex w-8 items-center justify-center text-sm text-foreground-400" />
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                placeholder={searchPlaceholder}
                className="h-8 w-full rounded-md border border-background-200 bg-background-50 pl-8 pr-7 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              />
              {loading || isDataLoading ? (
                <span className="pointer-events-none absolute inset-y-0 right-0 flex w-7 items-center justify-center text-primary-600">
                  <i className="ri-loader-4-line animate-spin text-sm" />
                </span>
              ) : query ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setQuery("");
                    setPage(1);
                  }}
                  className="absolute inset-y-0 right-0 flex w-7 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="ml-auto flex items-center gap-1.5">
            {showDensityToggle && (
              <button
                type="button"
                title="Toggle row density"
                onClick={() => {
                  const next = density === "compact" ? "comfortable" : "compact";
                  setDensity(next);
                  ui.setDensity(next);
                }}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-align-justify text-base" />
              </button>
            )}
            {showColumnVisibility && (
              <Dropdown
                trigger={() => (
                  <button
                    type="button"
                    title="Column visibility"
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-500 hover:bg-background-100"
                  >
                    <i className="ri-settings-3-line text-base" />
                  </button>
                )}
                items={columnMenuItems}
              />
            )}
            {exportable && (
              <Button variant="outline" size="sm" icon={<i className="ri-download-2-line" />} onClick={handleExport}>
                Export
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Filter row */}
      {visibleColumns.some((c) => c.filterable) ? (
        <div className="no-print flex flex-wrap gap-2 border-b border-background-100 bg-background-100/40 px-3 py-2">
          {selectable && <span className="w-10" />}
          {visibleColumns
            .filter((c) => c.filterable)
            .map((c) => (
              <div key={c.id} className="w-40">
                <Select
                  value={filters[c.id] ?? ""}
                  onChange={(v) => setFilter(c.id, v)}
                  placeholder="All"
                  options={[{ value: "", label: "All" }, ...(c.filterOptions ?? [])]}
                />
              </div>
            ))}
          {Object.keys(filters).some((k) => filters[k]) && (
            <button type="button" onClick={() => setFilters({})} className="cursor-pointer self-center text-xs font-medium text-primary-600 hover:underline">
              Reset
            </button>
          )}
        </div>
      ) : null}

      {/* Body states */}
      {isDataLoading ? (
        <SkeletonTable rows={6} columns={visibleColumns.length + (selectable ? 1 : 0)} />
      ) : error ? (
        <ErrorState message={error} onRetry={onRetry} />
      ) : pageRows.length === 0 ? (
        <EmptyState
          title={emptyMessage}
          description={emptyDescription}
          action={
            emptyAction ? (
              <Button size="sm" variant="outline" icon={<i className="ri-add-line" />} onClick={emptyAction.onClick}>
                {emptyAction.label}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="overflow-auto rounded-b-lg print:overflow-visible">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 print:static print:table-header-group">
              <tr className="bg-background-100/80 backdrop-blur print:bg-background-100">
                {selectable && (
                  <th className="w-10 px-3 py-2 print:hidden">
                    <input type="checkbox" checked={allPageSelected} onChange={toggleSelectPage} className="h-4 w-4 cursor-pointer accent-primary-600" />
                  </th>
                )}
                {visibleColumns.map((c) => (
                  <th
                    key={c.id}
                    style={c.width ? { width: c.width } : undefined}
                    className={cn(
                      "whitespace-nowrap px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground-500 print:text-foreground-700",
                      alignClass(c.align),
                      c.sortable && "cursor-pointer select-none hover:text-foreground-800",
                      c.headerClassName
                    )}
                    onClick={() => c.sortable && toggleSort(c)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.header}
                      {c.sortable && sort?.id === c.id ? (
                        <i className={cn("text-xs text-primary-600 print:hidden", sort.dir === "asc" ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line")} />
                      ) : null}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Screen body (paginated view for normal browsing) */}
            <tbody className="print:hidden">
              {pageRows.map((row, i) => {
                const key = rowKey(row);
                const isSelected = selected.has(key);
                const isActive = onRowClick && i === activeIndex;
                return (
                  <tr
                    key={key}
                    onClick={() => {
                      setActiveIndex(i);
                      onRowClick?.(row);
                    }}
                    onMouseEnter={() => onRowClick && setActiveIndex(i)}
                    className={cn(
                      "border-t border-background-100 transition-colors",
                      DENSITY_ROW[density],
                      onRowClick ? "cursor-pointer" : "cursor-default",
                      isActive ? "bg-primary-50/60" : isSelected ? "bg-primary-50/50" : "hover:bg-background-100/60"
                    )}
                  >
                    {selectable && (
                      <td className="px-3">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleRow(key)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 cursor-pointer accent-primary-600" />
                      </td>
                    )}
                    {visibleColumns.map((c) => (
                      <td key={c.id} className={cn("px-3 text-foreground-800", alignClass(c.align), c.className)}>
                        {c.accessor(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>

            {/* Print body (all sorted/filtered rows without pagination cutoffs) */}
            <tbody className="hidden print:table-row-group">
              {sortedRows.map((row) => {
                const key = rowKey(row);
                return (
                  <tr
                    key={key}
                    className="border-t border-background-200 print:break-inside-avoid"
                  >
                    {selectable && <td className="px-3 print:hidden" />}
                    {visibleColumns.map((c) => (
                      <td key={c.id} className={cn("px-3 py-1.5 text-foreground-900 print:break-inside-avoid", alignClass(c.align), c.className)}>
                        {c.accessor(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>

            {showTotals ? (
              <tfoot className="print:table-footer-group">
                <tr className="border-t-2 border-background-200 bg-background-100/60 font-semibold print:break-inside-avoid">
                  {selectable && <td className="px-3 py-2 print:hidden" />}
                  {visibleColumns.map((c, ci) => (
                    <td key={c.id} className={cn("px-3 py-2 text-foreground-900", alignClass(c.align))}>
                      {ci === 0 ? (
                        <span className="text-xs uppercase tracking-wide text-foreground-500">{totalLabel}</span>
                      ) : (
                        c.footerValue?.(sortedRows)
                      )}
                    </td>
                  ))}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isDataLoading && !error && total > 0 ? (
        <div className="no-print flex flex-wrap items-center justify-between gap-2 border-t border-background-100 px-3 py-2.5 text-xs text-foreground-600">
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap">Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="h-8 cursor-pointer rounded-md border border-background-200 bg-background-50 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="tnum whitespace-nowrap">
              {total === 0 ? 0 : (safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, total)} of {total}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" disabled={safePage === 1} onClick={() => setPage(safePage - 1)} aria-label="Previous page" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:cursor-not-allowed disabled:text-foreground-300">
              <i className="ri-arrow-left-s-line text-base" />
            </button>
            <span className="tnum px-1 text-foreground-700">Page {safePage} / {pageCount}</span>
            <button type="button" disabled={safePage === pageCount} onClick={() => setPage(safePage + 1)} aria-label="Next page" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:cursor-not-allowed disabled:text-foreground-300">
              <i className="ri-arrow-right-s-line text-base" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}